/**
 * The піч, as a thing that burns rather than a place you stand.
 *
 * Until now the stove was a proximity gate: stand near one and certain recipes
 * became craftable, instantly and for free. That was honest while there was
 * nothing to burn - the note on the glass recipe said as much, "the straw is
 * the fuel, which is also the honest answer to why sand becomes glass when
 * there is no furnace in the game yet". There is one now.
 *
 * Three slots and two clocks. Fuel burns down; while it burns, whatever is in
 * the input cooks. Both clocks run whether or not anybody is watching, because
 * a furnace you have to stand over is a furnace nobody uses.
 *
 * The shaping recipes stay where they are. You shape a pot at the stove and
 * fire it at the stove; smelting is for what only fire can do, which is metal
 * out of rock.
 */

import { Inventory, MAX_STACK } from "../inventory/inventory.js";
import type { ItemStack } from "../inventory/inventory.js";
import { PlainItemId } from "../block/items.js";
import { BlockId } from "../block/registry.js";

export const FURNACE_SLOTS = 3;
/** What is being smelted. */
export const SLOT_INPUT = 0;
/** What is burning. */
export const SLOT_FUEL = 1;
/** What has come out. */
export const SLOT_OUTPUT = 2;

/** Seconds of fire to smelt one thing. */
export const SMELT_SECONDS = 10;

/**
 * How long a thing burns, in seconds.
 *
 * Straw is kindling, wood is wood, and the two that matter are the two this
 * country actually burned: peat cut out of a bog, and Donbas coal. Coal is
 * worth eight smelts and peat three, which is what makes it worth going deep
 * for rather than digging the nearest wetland.
 */
export const FUEL: Readonly<Record<number, number>> = {
  [BlockId.Thatch]: 4,
  [PlainItemId.Stick]: 5,
  [BlockId.OakPlanks]: 15,
  [BlockId.AcaciaPlanks]: 15,
  [BlockId.OakLog]: 15,
  [BlockId.AcaciaLog]: 15,
  [PlainItemId.Peat]: 30,
  [PlainItemId.Coal]: 80,
};

export function fuelSeconds(id: number): number {
  return FUEL[id] ?? 0;
}

/**
 * What comes out of what.
 *
 * Deliberately only the two ores a хатня піч could actually reduce. Titanium
 * is not on the list and should not be: ilmenite does not become metal in a
 * stove, it becomes metal in a chlorination plant, and pretending otherwise
 * would be the one piece of this game's geology that is a lie.
 */
export const SMELTING: Readonly<Record<number, number>> = {
  [PlainItemId.IronOre]: PlainItemId.IronIngot,
  [PlainItemId.ManganeseOre]: PlainItemId.Manganese,
  // Cooking, in the same fire and by the same rule. A піч that smelts ore but
  // cannot fry a piece of сало would be a furnace, and this is a stove.
  [PlainItemId.RawPork]: PlainItemId.Pork,
  [PlainItemId.RawBeef]: PlainItemId.Beef,
  [PlainItemId.RawChicken]: PlainItemId.Chicken,
  [PlainItemId.RawKaras]: PlainItemId.Karas,
};

export function smeltsTo(id: number): number | null {
  return SMELTING[id] ?? null;
}

/**
 * Whether a player may put `itemId` into this slot by hand.
 *
 * The output is take-only: the stove puts things there and nobody else does.
 * Without this an ingot dropped into it would be smelted back down or simply
 * swallowed by the next thing that came out.
 */
export function furnaceAccepts(slot: number, itemId: number): boolean {
  if (slot === SLOT_OUTPUT) return false;
  if (slot === SLOT_FUEL) return fuelSeconds(itemId) > 0;
  return smeltsTo(itemId) !== null;
}

export interface StoredFurnace {
  x: number;
  y: number;
  z: number;
  slots: (ItemStack | null)[];
  burn: number;
  burnMax: number;
  cook: number;
}

export class Furnace {
  readonly items = new Inventory(FURNACE_SLOTS);
  /** Seconds of fire left in what is currently burning. */
  burn = 0;
  /** What that fuel was worth, so the flame can be drawn as a fraction. */
  burnMax = 0;
  /** Seconds the current thing has been cooking. */
  cook = 0;

  get lit(): boolean {
    return this.burn > 0;
  }

  /** Can what is in the input become what is in the output? */
  private ready(): number | null {
    const input = this.items.get(SLOT_INPUT);
    if (input === null) return null;
    const result = smeltsTo(input.id);
    if (result === null) return null;

    const out = this.items.get(SLOT_OUTPUT);
    if (out === null) return result;
    if (out.id !== result || out.count >= MAX_STACK) return null;
    return result;
    return result;
  }

  /**
   * Run the fire for `dt` seconds.
   *
   * Fuel is only spent when there is something to cook, so a stove left
   * burning with an empty input does not eat a bag of coal while you are
   * away - it goes out and waits.
   */
  step(dt: number): void {
    if (this.burn > 0) this.burn = Math.max(0, this.burn - dt);

    const result = this.ready();

    if (this.burn <= 0 && result !== null) {
      const fuel = this.items.get(SLOT_FUEL);
      const worth = fuel === null ? 0 : fuelSeconds(fuel.id);
      if (worth > 0) {
        this.items.takeFromSlot(SLOT_FUEL, 1);
        this.burn = worth;
        this.burnMax = worth;
      }
    }

    if (result === null || this.burn <= 0) {
      // Nothing to cook, or nothing to cook it with. Progress bleeds away
      // rather than being kept: a half-smelted ore that waits a week for fuel
      // and then finishes instantly is a stove nobody has to plan around.
      this.cook = Math.max(0, this.cook - dt);
      return;
    }

    this.cook += dt;
    if (this.cook < SMELT_SECONDS) return;

    this.cook -= SMELT_SECONDS;
    this.items.takeFromSlot(SLOT_INPUT, 1);
    const out = this.items.get(SLOT_OUTPUT);
    if (out === null) this.items.set(SLOT_OUTPUT, { id: result, count: 1 });
    else out.count += 1;
  }

  /** Everything in it, for when the stove is broken. */
  contents(): ItemStack[] {
    return this.items.slots.filter((s): s is ItemStack => s !== null);
  }
}

function key(x: number, y: number, z: number): string {
  return `${x},${y},${z}`;
}

/** Every stove in the world, keyed by where it stands. */
export class Furnaces {
  private readonly cells = new Map<string, Furnace>();

  get size(): number {
    return this.cells.size;
  }

  at(x: number, y: number, z: number): Furnace | null {
    return this.cells.get(key(x, y, z)) ?? null;
  }

  open(x: number, y: number, z: number): Furnace {
    const k = key(x, y, z);
    const found = this.cells.get(k);
    if (found !== undefined) return found;
    const made = new Furnace();
    this.cells.set(k, made);
    return made;
  }

  clear(x: number, y: number, z: number): Furnace | null {
    const k = key(x, y, z);
    const found = this.cells.get(k) ?? null;
    this.cells.delete(k);
    return found;
  }

  /**
   * Run every fire, and say which ones caught or went out.
   *
   * The block a stove *is* changes when it lights, because that is the only
   * thing the mesher can see - so somebody has to notice the moment it
   * happens, and this is the only place that runs every fire every tick.
   *
   * @returns just the ones that changed. Almost always none.
   */
  step(dt: number): { x: number; y: number; z: number; lit: boolean }[] {
    const changed: { x: number; y: number; z: number; lit: boolean }[] = [];
    for (const [at, furnace] of this.cells) {
      const was = furnace.lit;
      furnace.step(dt);
      if (furnace.lit === was) continue;
      const [x, y, z] = at.split(",").map(Number) as [number, number, number];
      changed.push({ x, y, z, lit: furnace.lit });
    }
    return changed;
  }

  /** Which cells are alight, so the renderer can show it. */
  lit(): { x: number; y: number; z: number }[] {
    const out: { x: number; y: number; z: number }[] = [];
    for (const [k, furnace] of this.cells) {
      if (!furnace.lit) continue;
      const [x, y, z] = k.split(",").map(Number) as [number, number, number];
      out.push({ x, y, z });
    }
    return out;
  }

  save(): StoredFurnace[] {
    const out: StoredFurnace[] = [];
    for (const [k, furnace] of this.cells) {
      const empty = furnace.items.slots.every((s) => s === null);
      if (empty && furnace.burn <= 0) continue;
      const [x, y, z] = k.split(",").map(Number) as [number, number, number];
      out.push({
        x, y, z,
        slots: furnace.items.slots.map((s) => (s === null ? null : { ...s })),
        burn: furnace.burn,
        burnMax: furnace.burnMax,
        cook: furnace.cook,
      });
    }
    return out;
  }

  load(list: readonly StoredFurnace[]): void {
    this.cells.clear();
    for (const stored of list) {
      const furnace = this.open(stored.x, stored.y, stored.z);
      stored.slots.forEach((stack, i) => {
        if (i < FURNACE_SLOTS) furnace.items.set(i, stack === null ? null : { ...stack });
      });
      furnace.burn = stored.burn;
      furnace.burnMax = stored.burnMax;
      furnace.cook = stored.cook;
    }
  }
}
